import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { green } from '@mui/material/colors';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { t } from 'i18next';

export interface ContatosDialogProps {
  open: boolean;
  onClose: () => void;
  /** Ex.: +5511999999999 */
  vendasPhone?: string;
  /** Ex.: +5511988888888 */
  suportePhone?: string;
}

function waLink(phone?: string, text?: string) {
  if (!phone) return undefined;
  const msg = text ? `?text=${encodeURIComponent(text)}` : '';
  // usa wa.me para abrir direto no app / web
  return `https://wa.me/${phone.replace(/\D/g, '')}${msg}`;
}

export default function ContatosDialog({
  open,
  onClose,
  vendasPhone = '+5586988845668',
  suportePhone = '+5586989070786',
}: ContatosDialogProps) {
  const items = [
    {
      key: 'vendas',
      title: `${t('ContatosDialog.vendasTitle')}`,
      secondary: `${t('ContatosDialog.vendasDescription')}`,
      href: waLink(vendasPhone, `${t('ContatosDialog.vendasWaText')}`),
    },
    {
      key: 'suporte',
      title: `${t('ContatosDialog.suporteTitle')}`,
      secondary:
        `${t('ContatosDialog.suporteDescription')}`,
      href: waLink(
        suportePhone,
        `${t('ContatosDialog.suporteWaText')}`
      ),
    },
  ];

  const handleClick = (href?: string) => {
    if (href) window.open(href, '_blank', 'noopener,noreferrer');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 8,
        sx: {
          borderRadius: 4,
          px: { xs: 1, sm: 2 },
          py: 1,
          backdropFilter: 'blur(6px)',
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
        <Typography variant="h6" fontWeight={700}>
            {t('ContatosDialog.contatosDialogTitle')}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', mt: 0.5, mb: 1.5 }}
        >
            {t('ContatosDialog.contatosDialogDescription')}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <List disablePadding>
          {items.map((item, idx) => (
            <Box key={item.key}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleClick(item.href)}
                  sx={{
                    borderRadius: 2,
                    px: 2,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <WhatsAppIcon sx={{ color: green[600], width: 32, height: 32 }} />
                  </ListItemIcon>

                  <ListItemText
                    primary={
                      <Typography variant="h6" fontWeight={800}>
                        {item.title}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {item.secondary}
                      </Typography>
                    }
                  />

                  <OpenInNewRoundedIcon
                    fontSize="small"
                    sx={{ color: 'text.disabled', ml: 1 }}
                    aria-hidden
                  />
                </ListItemButton>
              </ListItem>

              {idx < items.length - 1 && (
                <Divider sx={{ my: 0.5, opacity: 0.6 }} component="li" />
              )}
            </Box>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  );
}
